import React, { useState } from 'react'

import * as Content from '../Content'
import * as Form from '../Form'
import * as Input from '../Input'
import { View } from './View'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar'
import { faStar as faStarOutline } from '@fortawesome/free-solid-svg-icons/faStar'

import { concatStyles } from 'utils'

const FLEX_COLUMN = 'flex flex-col'
const FLEX_CONTAINER_STYLES = `${FLEX_COLUMN} p-8 pt-0`

const RATING_CONTAINER_STYLES = 'flex mb-4'

const ACTIVE_RATING_BACKGROUND_STYLES = 'bg-accent'
const DEFAULT_RATING_BACKGROUND_STYLES = 'bg-contentareabackground'

const ACTIVE_RATING_COLOR_STYLES = 'text-contentareabackground'
const DEFAULT_RATING_COLOR_STYLES = 'text-accent'

const RATING_STYLES =
  'flex items-center text-xl mr-1 w-10 h-10 border border-solid border-accent rounded-full'

const COMMENT_STYLES = `${FLEX_COLUMN} mb-2 w-full`

export interface IRatingProps {
  title: string
  description: string
  onDismiss: () => void
  onConfirm?: (score?: number, comment?: string | null) => void
  score?: number
  allowComment?: boolean
  commentLabel?: string
  cancelLabel?: string
  confirmLabel?: string
  loading?: boolean
}

export const Rating: React.FunctionComponent<IRatingProps> = ({
  title,
  description,
  onDismiss,
  onConfirm,
  score: ratingScore,
  allowComment,
  commentLabel,
  confirmLabel,
  loading,
}) => {
  const [comment, setComment] = useState(null as string | null)
  const [score, setScore] = useState(ratingScore ?? 0)

  const handleConfirm = () => onConfirm?.(score, comment)
  const handleChange: React.ChangeEventHandler = (e) => {
    setComment((e.target as HTMLTextAreaElement).value)
  }
  const handleScore = (newScore: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setScore(newScore)
  }

  return (
    <View onDismiss={onDismiss} loading={loading}>
      <div className={FLEX_CONTAINER_STYLES}>
        <Content.HeadingSmall>{title}</Content.HeadingSmall>
        <Content.Body>{description}</Content.Body>
        <div className={RATING_CONTAINER_STYLES}>
          {new Array(5).fill('').map((_, i) => {
            const active = score - 1 >= i
            return (
              <button
                className={concatStyles([
                  RATING_STYLES,
                  active
                    ? ACTIVE_RATING_BACKGROUND_STYLES
                    : DEFAULT_RATING_BACKGROUND_STYLES,
                  active
                    ? ACTIVE_RATING_COLOR_STYLES
                    : DEFAULT_RATING_COLOR_STYLES,
                ])}
                key={`ratingScore${i}`}
                onClick={handleScore(i + 1) as any}
              >
                <p className="m-auto">
                  <FontAwesomeIcon icon={active ? faStar : faStarOutline} />
                </p>
              </button>
            )
          })}
        </div>
        {allowComment ? (
          <div className={COMMENT_STYLES}>
            <Form.Label>
              {commentLabel ??
                'Do you want to provide any feedback? (optional):'}
            </Form.Label>
            <Input.TextArea value={comment ?? ''} onChange={handleChange} />
          </div>
        ) : null}
        <Form.ButtonGroup direction="row">
          {onConfirm ? (
            <Form.Button onClick={handleConfirm} disabled={score < 1}>
              <span>{confirmLabel ?? 'Confirm'}</span>
            </Form.Button>
          ) : null}
        </Form.ButtonGroup>
      </div>
    </View>
  )
}
